import React, { useEffect } from 'react';
import { observer, Provider, useLocalStore } from 'mobx-react';
import { Route, Switch } from 'react-router';
import { needle, routingStore } from '../../../common/stores/routing';
import { OverlayPage } from '../../../commonv3/components/molecules/layout/OverlayPage';
import { PageForMyPageRenewal } from '../../components/atoms/applications/page';
import { PageHeader } from '../../components/atoms/layout/PageHeader';
import { useNavigation } from '../../components/hocs/withNavigation';
import { MokumokuSessionListPane } from '../../components/organisms/sessions/MokumokuSessionListPane';
import { SessionReservationPane } from '../../components/organisms/sessions/SessionReservationPane';
import { restrictAccessForUser } from '../../../commonv3/hooks/restrictAccessForUser';
import {
  V3Routes,
  NOTION_MOKUMOKU_GUIDE_URL,
  GOOGLE_SLIDE_LIST_OF_TA_URL,
  CommmuneRoute,
  GOOGLE_SLIDE_HOW_TO_USE_MOKUMOKU_URL,
  GOOGLE_SLIDE_TYPE_OF_MOKUMOKU_URL,
  NOTION_OTHER_MOKUMOKU_TYPE_URL,
  COMMMUNE_MOKUMOKU_STUDY_ROOM_PATH,
} from '../../routes/index';
import { FooterSpecs, useFooter } from '../../states/FooterState';
import { NavigationSpecs } from '../../states/NavigationState';
import { MembershipPlanGroupKey } from '../../types/membershipPlans';
import { MokumokuPageState } from './states/MokumokuPageState';
import { SessionReservationCompletePane } from '../../components/organisms/sessions/SessionReservationCompletePane';
import { AboutSection } from '../../components/atoms/sections/AboutSection';
import Image from './images/about-mokumoku-section-image.png';
import BenefitsOfAttendingMokumokuImage from './images/benefits-of-attending-mokumoku-image.png';
import TypeOfMokumokuImage from './images/type-of-mokumoku-image.png';
import MokumokuGuideImage from './images/mokumoku-guide-image.png';
import ListOfTaImage from './images/list-of-ta-image.png';
import MokumokuStudyRoomImage from './images/mokumoku-study-room-image.png';
import './mokumokuPage.module.scss';
import { RouterStore } from '../../../common/mobx-react-router';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { PageSection } from '../../components/atoms/sections/PageSection';
import { PageSectionContent } from '../../components/atoms/sections/PageSectionContent';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';
import { SquareButton } from '../../../commonv3/components/atoms/buttons/SquareButton';
import { MokuMokuStudyRoom } from '../mokumoku_study_room/MokuMokuStudyRoom';
import { AnchorButton, LinkButton } from '@sheinc/shelikes-design-system';

type ReservationSectionProps = {
  loading: boolean;
  isEffectiveMultiCreatorUser: boolean;
  routing: RouterStore;
};

const ReservationSection = ({ loading, isEffectiveMultiCreatorUser, routing }: ReservationSectionProps) => {
  return (
    <div styleName="reservation-section">
      <div styleName="button-center">
        <SquareButton
          id={'mokumoku-reserve-button'}
          loading={loading}
          onReservationClick={() => routing.push(V3Routes.mokumokuSessions2(MembershipPlanGroupKey.LIKES))}
          label="もくもく会を予約する"
          buttonWidth={GlobalStatesV3.viewport.isSp ? '100%' : '310px'}
        />
      </div>
      {isEffectiveMultiCreatorUser && (
        <>
          <div styleName="mokumoku-reserve-for-multicreator-description">
            マルチクリエイターコース専用もくもく会予約はこちらから
          </div>
          <div styleName="button-center">
            <SquareButton
              id={'mokumoku-reserve-for-multicreator-button'}
              loading={loading}
              type="multicreator"
              onReservationClick={() => routing.push(V3Routes.mokumokuSessions2(MembershipPlanGroupKey.MULTICREATOR))}
              label="専用もくもく会を予約する"
              buttonWidth={GlobalStatesV3.viewport.isSp ? '100%' : '310px'}
            />
          </div>
        </>
      )}
    </div>
  );
};

const MokuMokuSelfStudyContent = () => {
  return (
    <>
      <div styleName="mokumoku-self-study-content">
        <div styleName="mokumoku-self-study-content-block">
          予約不要で24時間いつでもご利用可能なもくもくと作業に集中できる自習室(Zoom)です。
          「学習の習慣をつけたい」「他のシーメイトさんと同じ空間で作業をしたい」という方におすすめです。
          下記よりZoomに入って作業を始めてみましょう。
          <a
            styleName="mokumoku-page-link"
            href={V3Routes.home(GOOGLE_SLIDE_HOW_TO_USE_MOKUMOKU_URL).stringify()}
            target="_blank"
            data-testid="how-to-use-mokumoku-self-study-room-link"
          >
            ご利用方法についてはこちら。
          </a>
        </div>
        <div styleName="mokumoku-self-study-content-block">
          <img src={MokumokuStudyRoomImage} />
        </div>
      </div>
      <div styleName="button-wrap">
        <div>
          <AnchorButton
            variant="secondary"
            href={V3Routes.home(CommmuneRoute(COMMMUNE_MOKUMOKU_STUDY_ROOM_PATH)).stringify()}
            target="_blank"
            testId="commmune-mokumoku-study-room-button"
            fullWidth={true}
          >
            もくもく自習室への入室を宣言する
          </AnchorButton>
          <p styleName="button-description">コミュニティサイト「SHEstation」が開きます</p>
        </div>
        <div>
          <LinkButton variant="secondary" to={V3Routes.mokumokuStudyRoom2()} fullWidth={true}>
            もくもく自習室（Zoom）へ入室する
          </LinkButton>
          <p styleName="button-description">Zoomが開きます</p>
        </div>
      </div>
    </>
  );
};

type MokuMokuTypeDescriptionProps = {
  hasEffectiveMembershipToken: boolean;
};

const MokuMokuTypeDescription = ({ hasEffectiveMembershipToken }: MokuMokuTypeDescriptionProps) => {
  return (
    <>
      <div styleName="mokumoku-type-description-header">もくもく会の一例</div>
      <div styleName="mokumoku-type-description-block">
        <div styleName="mokumoku-type-description-title">はじめてのもくもく会</div>
        <div>
          Webデザインの学習を始めたばかりの人に参加してほしいもくもく会です。Photoshopの基本操作についての質問はこちらにご参加ください。（Zoomでの開催のみ）
        </div>
      </div>
      <div styleName="mokumoku-type-description-block">
        <div styleName="mokumoku-type-description-title">Webデザイン入門もくもく会</div>
        <div>
          Webデザイン入門コースを受講中の方におすすめです。取り組んでいる課題やデザインの進め方に関する質問がよく出ています。（Zoom、SHEstation、SHE各拠点での開催）
        </div>
      </div>
      <div styleName="mokumoku-type-description-block">
        <div styleName="mokumoku-type-description-title">実技試験もくもく会</div>
        <div>
          Webデザインコースの実技試験に悩んでいる方や質問をしたい方向け。コーディングの質問も対応しています。（Zoom、SHEstation、SHE各拠点での開催）
        </div>
      </div>
      {hasEffectiveMembershipToken && (
        <div>
          もくもく会は用途に合わせた種類があります。主にWebデザインコースのもくもく会となりますが、イベント形式で月に1〜2度、
          <a
            href={V3Routes.home(NOTION_OTHER_MOKUMOKU_TYPE_URL).stringify()}
            target="_blank"
            styleName="mokumoku-page-link"
            data-testid="other-mokumoku-type-link"
          >
            ライターやマーケティング、ブランディングのもくもく会
          </a>
          も実施しています。
        </div>
      )}
      <div>
        会によって難易度や開催方式が異なるため、学習スタイルや目標に合わせて、自分にぴったりのもくもく会を見つけましょう！詳細は以下のボタンからご確認ください。
      </div>
    </>
  );
};

const MoreUseContent = () => {
  return (
    <div styleName="more-use-content">
      <a
        href={V3Routes.home(NOTION_MOKUMOKU_GUIDE_URL).stringify()}
        target="_blank"
        data-testid="mokumoku-guide-banner"
      >
        <img src={MokumokuGuideImage} />
      </a>
      <a href={V3Routes.home(GOOGLE_SLIDE_LIST_OF_TA_URL).stringify()} target="_blank" data-testid="list-of-ta-banner">
        <img src={ListOfTaImage} />
      </a>
    </div>
  );
};

export const MokumokuPage = observer(() => {
  restrictAccessForUser();
  useNavigation(NavigationSpecs.side());
  useFooter(FooterSpecs.likes());

  const { pageState } = useLocalStore(() => {
    return {
      pageState: new MokumokuPageState(),
    };
  });
  useEffect(() => {
    pageState.init();
  }, []);

  const { loading } = pageState;
  const { currentUser, routing } = GlobalStatesV3;
  const hasEffectiveMembershipToken = currentUser.hasEffectiveMembershipToken();

  return (
    <>
      <PageForMyPageRenewal id="mokumoku-page">
        <div styleName="mokumoku-page-header">
          <BreadCrumb items={[{ text: 'もくもく会' }]} hasHome />
          <PageHeader title="STUDYROOM" testId="page-header-of-mokumoku" />
        </div>
        <div styleName="mokumoku-page-content">
          <AboutSection
            title="もくもく会とは"
            description="学習を進めたいシーメイトさんたちが集まって、わからないことを聞きながら、それぞれの学習を進める予約制の勉強会です。コース内のLESSONや課題を進める中で生まれた疑問点をティーチング・アシスタント(TA)に直接質問できるのが大きな魅力。ひとりでは学習を進めることが難しい方は学習習慣作りのきっかけにご活用ください。"
            image={Image}
            color="green"
          />
          <ReservationSection
            loading={loading}
            isEffectiveMultiCreatorUser={currentUser.isEffectiveMultiCreatorUser()}
            routing={routing}
          />
          <div>
            {hasEffectiveMembershipToken && (
              <PageSection title="もくもく自習室" testid="mokumoku-self-study-room-section">
                <PageSectionContent body={<MokuMokuSelfStudyContent />} />
              </PageSection>
            )}
            <PageSection title="もくもく会に参加するメリット" testid="benefits-section">
              <PageSectionContent
                image={BenefitsOfAttendingMokumokuImage}
                body="もくもく会では、自分で解決できなかった疑問をTAさんに直接聞くことができます。でも「何を聞いたらいいかわからないから…」と参加を躊躇しなくても大丈夫。まずは参加することで、他のシーメイトさんの質問を聞いて知識を広げたり、新しい視点を得られることもあります。また、もくもく会を予約することで、自分の学習時間を予め確保して学習習慣をつけることもできます。"
              />
            </PageSection>
            <PageSection title="もくもく会の種類" testid="type-section">
              <PageSectionContent
                image={TypeOfMokumokuImage}
                body={<MokuMokuTypeDescription hasEffectiveMembershipToken={hasEffectiveMembershipToken} />}
                buttonAttributes={[
                  {
                    text: 'もくもく会の種類の詳細を見る',
                    href: V3Routes.home(GOOGLE_SLIDE_TYPE_OF_MOKUMOKU_URL).stringify(),
                    testid: 'mokumoku-type-button',
                  },
                ]}
              />
            </PageSection>
            {hasEffectiveMembershipToken && (
              <PageSection title="もくもく会をもっと活用しよう" testid="more-section">
                <PageSectionContent body={<MoreUseContent />} />
              </PageSection>
            )}
          </div>
        </div>
      </PageForMyPageRenewal>

      <Provider homeState={pageState}>
        <OverlayPage
          open={needle(['sessions', 'mokumoku_study_room'], routingStore.location) !== undefined}
          onRequestClose={() => routingStore.push(V3Routes.mokumoku())}
        >
          <Switch>
            <Route exact path={'/mokumoku/sessions'} component={MokumokuSessionListPane} />
            <Route exact path={'/mokumoku/sessions/:sessionId/reservation'} component={SessionReservationPane} />
            <Route
              exact
              path={'/mokumoku/sessions/:sessionId/reservation_complete'}
              component={SessionReservationCompletePane}
            />
            <Route exact path={'/mokumoku/mokumoku_study_room'} component={MokuMokuStudyRoom} />
          </Switch>
        </OverlayPage>
      </Provider>
    </>
  );
});
